import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import i18n from '../i18n';
import '../App.css';
import Drawing from '../components/canvas/Drawing.js';
import PageBackground from '../components/common/PageBackground.js';
import * as api from "../api";
import {
    sendStrokes, subscribeToCanvas, subscribeToChat,
    sendMoveLayers, onRoomPrivateChange, setRoomPrivate, onUndoReplay, onUndoDelta,
    sendThumbnail, sendChatMessage, leaveRoom, onRoomInfo, onRoomOwner, onRoomFull, onNameTaken,
    sendGuessOptIn, onGuessRoundStart, onGuessRoundEnd, onGuessWord, onGuessHint, onGuessCorrect,
    onGuessUserList, onChatRateLimited, sendGuessVote, onGuessVoteAck, onGuessPeriodEnd, onGuessScore,
    sendAfkToggle,
    sendGuessSkip, onConnectionLostFromRoom, sendVoteKick, sendTransferOwnership, onKicked, sendWordChoice,
    setRoomAiAllowed, onAiBlocked, publishDrawing, onPublishDrawingAck,
} from "../api";
import {drawLine} from '../utils/drawLine.js';
import {playSound, playChuckleIfMatch} from '../utils/sounds.js';
import {generateUsername} from '../utils/utils.js';
import {containsProfanity} from '../utils/profanity.js';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {arrayMove} from "@dnd-kit/sortable";
import confetti from 'canvas-confetti';

const w = 980;
const h = 600;

// Thumbnail capture interval (ms).
const THUMBNAIL_INTERVAL = 15000;

export default function RoomPage() {
    const {t} = useTranslation();
    const {roomId} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gameType = searchParams.get('game') || 'guess';
    const drawTime = Number(searchParams.get('drawTime')) || 120;
    const minPlayers = Number(searchParams.get('minPlayers')) || 1;
    const everyoneDraws = searchParams.get('everyoneDraws') === '1';
    const wordSet = searchParams.getAll('wordSet').filter(Boolean);
    const isPrivateParam = searchParams.get('private') === '1';
    const maxUsers = Number(searchParams.get('maxUsers')) || 0;
    const totalRounds = Number(searchParams.get('totalRounds')) || 10;
    const lang = searchParams.get('lang') || i18n.language;
    const botKey = searchParams.get('botKey') || undefined;
    const userParam = searchParams.get('user') || '';
    const spectatorParam = searchParams.get('spectator') === '1';
    // Custom uploaded wordset words — stashed in sessionStorage by FrontPage to
    // avoid stuffing potentially large arrays into URL query params.
    const customWordsRef = useRef(null);
    const customWordsNameRef = useRef('');
    if (customWordsRef.current === null) {
        try {
            const key = 'suttu_custom_words_' + roomId;
            const raw = sessionStorage.getItem(key);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    // Legacy shape: array of words only
                    customWordsRef.current = parsed;
                } else if (parsed && Array.isArray(parsed.words)) {
                    customWordsRef.current = parsed.words;
                    customWordsNameRef.current = typeof parsed.name === 'string' ? parsed.name : '';
                } else {
                    customWordsRef.current = [];
                }
                sessionStorage.removeItem(key); // one-shot: only used on initial join
            } else {
                customWordsRef.current = [];
            }
        } catch (e) {
            customWordsRef.current = [];
        }
    }

    const [user, setUser] = useState(() => userParam || localStorage.getItem('suttu_nickname') || generateUsername());
    const [isAutoName, setIsAutoName] = useState(() => !userParam && !localStorage.getItem('suttu_nickname'));
    const [users, setUsers] = useState([]);
    const [appStep, setAppStep] = useState(() => userParam && userParam.length >= 3 ? 'canvas' : 'username'); // 'username' | 'canvas'
    const [nameError, setNameError] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [roomOwner, setRoomOwner] = useState(null);
    const [subscribed, setSubscribed] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [isSpectator, setIsSpectator] = useState(spectatorParam);

    const remoteStrokesRef = React.useRef([]);
    const localStrokesRef = React.useRef([]);
    const currentStrokeRef = React.useRef(null);
    const [layerList, setLayerList] = React.useState([]);
    const [layer, setLayer] = React.useState();
    const layerRef = React.useRef(layer);
    layerRef.current = layer;
    // Bot/automation hook: lets the Puppeteer bot add a new layer (which the
    // server places at the bottom of the stack and the existing add-layer
    // callback auto-selects). Returns a Promise that resolves with the new
    // layer name once setLayer has switched, or null if it timed out.
    React.useEffect(() => {
        window.__suttuAddLayer = () => new Promise(resolve => {
            const before = layerRef.current;
            try {
                api.sendAddLayer();
                console.log('[suttuAddLayer] emitted, before=', before);
            } catch (e) {
                console.log('[suttuAddLayer] emit failed', e);
                resolve(null);
                return;
            }
            const t0 = Date.now();
            const tick = () => {
                if (layerRef.current && layerRef.current !== before) {
                    console.log('[suttuAddLayer] switched to', layerRef.current);
                    resolve(layerRef.current);
                    return;
                }
                if (Date.now() - t0 > 3000) {
                    console.log('[suttuAddLayer] timeout, layerRef.current=', layerRef.current);
                    resolve(null);
                    return;
                }
                setTimeout(tick, 50);
            };
            setTimeout(tick, 50);
        });
        return () => { try { delete window.__suttuAddLayer; } catch (_) {} };
    }, []);
    const [canvases] = React.useState(new Map());
    const [thumbs] = useState(new Map());
    const [canvasVersion, setCanvasVersion] = useState(0);
    const [clearVersion, setClearVersion] = useState(0);
    // Increments each time OUR drawing round ends — signals Drawing to reset tools.
    const [drawerEndVersion, setDrawerEndVersion] = useState(0);

    // Local stroke history for delta undo support.
    // Map<layerName, strokeObject[]> — mirrors what's been drawn to canvases.
    const strokeHistoryRef = useRef(new Map());

    // Prediction canvas ref — rendered by Drawing component on top of all layers.
    const predictionCanvasRef = useRef(null);
    // Pending predicted strokes — drawn on prediction canvas, awaiting server ack.
    const pendingStrokesRef = useRef([]);

    // Replay animation state for intermission drawing playback.
    const replayAnimRef = useRef(null); // requestAnimationFrame ID
    const [replayActive, setReplayActive] = useState(false);

    // Authoritative room settings received from the server (replaces URL params).
    const [roomSettings, setRoomSettings] = useState(null);
    const effectiveEveryoneDraws = roomSettings ? !!roomSettings.everyoneDraws : everyoneDraws;
    const roomLang = roomSettings && roomSettings.lang ? roomSettings.lang : lang;
    const aiAvailable = roomSettings
        ? roomSettings.aiAvailable !== false && roomLang === 'en'
        : lang === 'en';
    const aiAllowed = aiAvailable && (roomSettings ? roomSettings.aiAllowed !== false : !isPrivateParam);

    // ── Guess-mode state ────────────────────────────────────────────────────
    const [guessRound, setGuessRound] = useState({
        active: false, drawer: null, timeLeft: 0, wordPattern: '',
        word: null, // only set for the drawer
        roundNumber: 0, totalRounds: 10, wordSet: null,
        choosing: false, // true while drawer picks a word
        choiceTime: 15, // seconds allowed to pick a word
        wordChoices: null, // [{word, wordSet}] only for drawer
    });
    const [guessIntermission, setGuessIntermission] = useState({
        active: false, word: null, winner: null, nextDrawer: null, message: '',
    });
    const [guessUserList, setGuessUserList] = useState([]);
    const [selfOptedIn, setSelfOptedIn] = useState(false);
    const [chatRateLimited, setChatRateLimited] = useState(false);
    const [myVote, setMyVote] = useState(null); // current user's vote for this intermission
    const [correctGuessers, setCorrectGuessers] = useState(new Set()); // users who guessed correctly this round
    const [publishPending, setPublishPending] = useState(false);
    const [publishCount, setPublishCount] = useState(0);
    const [publishPopup, setPublishPopup] = useState(false);
    const [hasEnoughStrokes, setHasEnoughStrokes] = useState(false);
    const MIN_STROKES_TO_PUBLISH = 100;
    const guessRoundRef = useRef(guessRound);
    guessRoundRef.current = guessRound;
    const correctGuessersRef = useRef(new Set());
    correctGuessersRef.current = correctGuessers;
    const readyForSounds = useRef(false);

    const checkStrokeCount = useCallback(() => {
        let total = 0;
        for (const [, arr] of strokeHistoryRef.current) total += arr.length;
        setHasEnoughStrokes(total >= MIN_STROKES_TO_PUBLISH);
    }, [MIN_STROKES_TO_PUBLISH]);

    const isGuessMode = roomSettings ? roomSettings.gameType === 'guess' : gameType === 'guess';
    const isDrawer = isGuessMode && guessRound.active && guessRound.drawer === user;
    // During active round: only drawer can draw.
    // During choosing phase: nobody can draw yet.
    // During intermission: only previous drawer (guessRound.drawer still set) can draw.
    // Otherwise (free-for-all / waiting): anyone can draw.
    const drawingDisabled = isSpectator || replayActive || (isGuessMode && (
        guessRound.choosing ||
        (guessRound.active && guessRound.drawer !== user) ||
        (guessIntermission.active && guessRound.drawer !== user)
    ));

    // Lock body scroll when inside the room canvas.
    useEffect(() => {
        if (appStep === 'canvas') {
            document.body.classList.add('room-no-scroll');
        }
        return () => document.body.classList.remove('room-no-scroll');
    }, [appStep]);

    // Countdown timer for guess rounds
    useEffect(() => {
        if (!guessRound.active || guessRound.timeLeft <= 0) return;
        const interval = setInterval(() => {
            setGuessRound(prev => {
                if (!prev.active || prev.timeLeft <= 1) {
                    clearInterval(interval);
                    return {...prev, timeLeft: 0};
                }
                return {...prev, timeLeft: prev.timeLeft - 1};
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [guessRound.active, guessRound.drawer]); // restart when a new round begins

    // Tick tock sound for last 10 seconds
    useEffect(() => {
        if (guessRound.active && guessRound.timeLeft > 0 && guessRound.timeLeft <= 10) {
            playSound('tick_tock');
        }
    }, [guessRound.timeLeft, guessRound.active]);

    // Redraw thumbnails on a fast interval so they stay up-to-date
    // during both local and remote drawing.
    const redrawThumbs = useCallback(() => {
        layerList.forEach((layerName) => {
            const thumbCanvas = thumbs.get(layerName);
            const src = canvases.get(layerName);
            if (thumbCanvas && src) {
                const ctx = thumbCanvas.getContext("2d");
                ctx.imageSmoothingEnabled = false;
                ctx.clearRect(0, 0, 140, 100);
                ctx.filter = 'blur(0.4px)';
                ctx.drawImage(src, 0, 0, 140, 100);
                ctx.filter = 'none';
            }
        });
    }, [layerList, thumbs, canvases]);

    useEffect(() => {
        redrawThumbs();
    }, [canvasVersion, layerList, redrawThumbs]);

    useEffect(() => {
        const id = setInterval(redrawThumbs, 200);
        return () => clearInterval(id);
    }, [redrawThumbs]);

    const canvasRef = useCallback((value, node) => {
        if (node !== null && canvases.get(value) !== node) {
            canvases.set(value, node);
            setCanvasVersion(v => v + 1);
            // Drain any queued remote strokes that were waiting for this canvas.
            const queue = remoteStrokesRef.current;
            if (queue.length > 0) {
                const remaining = [];
                for (const s of queue) {
                    if (canvases.has(s.l)) {
                        drawLine(canvases, s, s.t);
                    } else {
                        remaining.push(s);
                    }
                }
                remoteStrokesRef.current = remaining;
            }
        }
    }, [canvases]);

    const thumbRef = useCallback((value, node) => {
        if (node && thumbs.get(value) !== node) {
            thumbs.set(value, node);
            setCanvasVersion(v => v + 1);
        }
    }, [thumbs]);

    const prevLayerListRef = useRef([]);
    useEffect(() => {
        if (layer && layerList.length > 0 && !layerList.includes(layer)) {
            // Active layer was deleted — select the next layer down, or the one above if
            // the deleted layer was last.
            const prevIndex = prevLayerListRef.current.indexOf(layer);
            const next = layerList[prevIndex] !== undefined
                ? layerList[prevIndex]
                : (layerList[prevIndex - 1] !== undefined ? layerList[prevIndex - 1] : layerList[0]);
            setLayer(next);
        }
        prevLayerListRef.current = layerList;
    }, [layerList, layer]);

    // ── Periodically capture and send a low-res thumbnail ──────────────
    useEffect(() => {
        if (appStep !== 'canvas') return;
        const interval = setInterval(() => {
            if (canvases.size === 0) return;
            // Skip if no strokes on any layer
            const history = strokeHistoryRef.current;
            const hasStrokes = history.size > 0 && Array.from(history.values()).some(a => a.length > 0);
            if (!hasStrokes) return;
            // Composite all layers onto an off-screen canvas.
            const offscreen = document.createElement('canvas');
            offscreen.width = 320;
            offscreen.height = 196;
            const ctx = offscreen.getContext('2d');
            // White background so transparency doesn't show as black.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, 320, 196);
            // Draw layers bottom-to-top using layerList (respects reorder).
            for (let i = layerList.length - 1; i >= 0; i--) {
                const src = canvases.get(layerList[i]);
                if (src) ctx.drawImage(src, 0, 0, 320, 196);
            }
            const data = offscreen.toDataURL('image/jpeg', 0.55);
            sendThumbnail({data});
        }, THUMBNAIL_INTERVAL);
        return () => clearInterval(interval);
    }, [appStep, canvases, layerList]);

    const latestUsers = useRef(users);

    useEffect(() => {
        latestUsers.current = users;

        function onMessageCallback(msg) {
            const botNames = latestUsers.current
                .filter(u => u && u.isBot)
                .map(u => u.name);
            const withBotFlags = msg.map(m => ({
                ...m,
                isBot: !!(m.isBot || (m.user && botNames.indexOf(m.user) !== -1)),
            }));
            setChatMessages(c => [...c, ...withBotFlags]);
            const elem = document.getElementById('messages');
            if (elem) elem.scrollTop = elem.scrollHeight;
            if (readyForSounds.current) msg.forEach(m => { if (m.user && m.message) playChuckleIfMatch(m.message); });
        }

        function onUserConnectionCallback(msg) {
            if (msg.type === "connect") {
                setChatMessages(c => [...c, {message: t('roomPage.joined', {name: msg.name})}]);
                setUsers(c => {
                    const next = c.filter(u => u.name !== msg.name).concat({name: msg.name, isBot: !!msg.isBot, spectator: !!msg.spectator});
                    latestUsers.current = next;
                    return next;
                });
                if (readyForSounds.current) playSound('join');
            } else {
                setChatMessages(c => [...c, {message: t('roomPage.left', {name: msg.name})}]);
                setUsers(c => {
                    const next = c.filter(u => u.name !== msg.name);
                    latestUsers.current = next;
                    return next;
                });
                if (readyForSounds.current) playSound('leave');
            }
        }

        function onUserHistoryCallback(msg) {
            const others = msg.map(u => ({name: u.name, isBot: !!u.isBot, spectator: !!u.spectator}));
            const next = [...others, {name: user}];
            latestUsers.current = next;
            setUsers(next);
            if (msg.length === 0) {
                setChatMessages(c => [...c, {message: t('roomPage.noOnlineUsers')}]);
                readyForSounds.current = true;
                return;
            }
            let string = t('roomPage.onlineUsers', {list: ''});
            for (let i = 0; i < msg.length; i++) {
                string += msg[i].name + (i !== msg.length - 1 ? ", " : "");
            }
            setChatMessages(c => [...c, {message: string}]);
            readyForSounds.current = true;
        }

        function onStrokesCallback(msg) {
            if (msg && msg.length > 0) {
                // Track in local stroke history for delta undo
                const history = strokeHistoryRef.current;
                for (const s of msg) {
                    const layerName = s.l;
                    if (!history.has(layerName)) history.set(layerName, []);
                    history.get(layerName).push(s);
                }
                // Draw immediately if canvases are ready, else queue
                const queue = remoteStrokesRef.current;
                const pending = queue.length > 0 ? [...queue, ...msg] : msg;
                let allReady = true;
                for (const s of pending) {
                    if (canvases.has(s.l)) {
                        drawLine(canvases, s, s.t);
                    } else {
                        allReady = false;
                    }
                }
                remoteStrokesRef.current = allReady ? [] : pending.filter(s => !canvases.has(s.l));
                checkStrokeCount();
            }
        }

        // Ack for our own strokes — server truth replaces local predictions
        function onStrokesAckCallback(msg) {
            if (msg && msg.length > 0) {
                const history = strokeHistoryRef.current;
                for (const s of msg) {
                    const layerName = s.l;
                    if (!history.has(layerName)) history.set(layerName, []);
                    history.get(layerName).push(s);
                    // Draw acked stroke to the real canvas (server-authoritative order)
                    drawLine(canvases, s, s.t);
                }
                // Dequeue acked strokes from pending predictions
                const ackLayer = msg[0].l;
                let toRemove = msg.length;
                pendingStrokesRef.current = pendingStrokesRef.current.filter(s => {
                    if (toRemove > 0 && s.l === ackLayer) { toRemove--; return false; }
                    return true;
                });
                reconcilePrediction();
                checkStrokeCount();
            }
        }

        function onAddLayersCallback(msg) {
            if (api.getPendingLayerAdd()) {
                api.clearPendingLayerAdd();
                setLayer(msg[msg.length - 1]);
            } else if (layerRef.current == null) {
                setLayer(msg[0]);
            }
            setLayerList(oldLayers => [...oldLayers, ...msg]);
        }

        function onRemoveLayerCallback(msg) {
            canvases.delete(msg);
            setLayerList(oldLayers => oldLayers.filter(e => e !== msg));
        }

        function onClearCallback() {
            setClearVersion(v => v + 1);
            remoteStrokesRef.current = [];
            strokeHistoryRef.current = new Map();
            canvases.forEach(element => {
                const ctx = element.getContext('2d');
                ctx.clearRect(0, 0, w, h);
            });
            pendingStrokesRef.current = [];
            reconcilePrediction();
            localStrokesRef.current = [];
            checkStrokeCount();
            currentStrokeRef.current = null;
        }

        function onResetLayersCallback(msg) {
            cancelReplay();
            // Remove canvases for deleted layers
            const keep = new Set(msg);
            for (const key of canvases.keys()) {
                if (!keep.has(key)) canvases.delete(key);
            }
            setLayerList(msg);
            setLayer(msg[0]);
        }

        function onMouseCallback(msg) {
            setUsers(c => {
                const index = c.findIndex(usr => usr.name === msg.name);
                if (index === -1) return c;
                const next = [...c];
                next[index] = {...next[index], x: msg.x, y: msg.y};
                latestUsers.current = next;
                return next;
            });
        }

        function onToolSettingsCallback(msg) {
            setUsers(c => {
                const index = c.findIndex(usr => usr.name === msg.name);
                if (index === -1) return c;
                const next = [...c];
                next[index] = {...next[index], ...msg};
                latestUsers.current = next;
                return next;
            });
        }

        function onMoveLayers(msg) {
            setLayerList(msg);
        }

        if (!subscribed && appStep === 'canvas' && roomId) {
            setSubscribed(true);
            subscribeToChat(
                (err, msg) => onMessageCallback(msg),
                (err, msg) => onUserConnectionCallback(msg),
                (err, msg) => onUserHistoryCallback(msg),
                user, roomId, gameType, drawTime, minPlayers, everyoneDraws, wordSet, isPrivateParam, maxUsers, totalRounds, lang, botKey, isSpectator, customWordsRef.current, customWordsNameRef.current);

            onRoomFull(data => {
                alert(t('roomPage.roomFull', {current: data.maxUsers, max: data.maxUsers}));
                navigate('/');
            });
            onNameTaken(() => {
                leaveRoom(); // tear down listeners so they don't stack on retry
                setNameError(t('roomPage.nameTaken'));
                setAppStep('username');
                setSubscribed(false);
            });

            subscribeToCanvas(
                (err, msg) => onStrokesCallback(msg),
                (err, msg) => onClearCallback(),
                (err, msg) => onMouseCallback(msg),
                (err, msg) => onToolSettingsCallback(msg),
                (err, msg) => onAddLayersCallback(msg),
                (err, msg) => onRemoveLayerCallback(msg),
                (err, msg) => onMoveLayers(msg),
                (err, msg) => onStrokesAckCallback(msg),
                (err, msg) => onResetLayersCallback(msg));
            onRoomPrivateChange(val => setIsPrivate(val));
            onRoomOwner(owner => setRoomOwner(owner));
            onRoomInfo(info => {
                setRoomSettings(info);
                if (info.everyoneDraws) setSelfOptedIn(true);
                if (info.totalRounds) setGuessRound(prev => ({ ...prev, totalRounds: info.totalRounds, roundNumber: info.roundNumber || prev.roundNumber }));
            });
            onUndoReplay(replayStrokes => {
                canvases.forEach(canvas => {
                    canvas.getContext('2d').clearRect(0, 0, w, h);
                });
                // Rebuild local stroke history from replay
                strokeHistoryRef.current = new Map();
                replayStrokes.forEach(stroke => {
                    drawLine(canvases, stroke, stroke.t);
                    const layerName = stroke.l;
                    const history = strokeHistoryRef.current;
                    if (!history.has(layerName)) history.set(layerName, []);
                    history.get(layerName).push(stroke);
                });
                checkStrokeCount();
            });
            onUndoDelta(data => {
                // Delta undo: remove SIDs from local history and redraw
                const sidsToRemove = new Set(data.sids);
                const history = strokeHistoryRef.current;
                for (const [layerName, layerStrokes] of history.entries()) {
                    history.set(layerName, layerStrokes.filter(s => !sidsToRemove.has(s.sid)));
                }
                // Clear all canvases and redraw from local history
                canvases.forEach(canvas => {
                    canvas.getContext('2d').clearRect(0, 0, w, h);
                });
                for (const [, layerStrokes] of history.entries()) {
                    for (const stroke of layerStrokes) {
                        drawLine(canvases, stroke, stroke.t);
                    }
                }
                checkStrokeCount();
            });

            // Guess-mode listeners
            onGuessRoundStart(data => {
                setGuessRound(prev => ({
                    active: !data.choosing,
                    drawer: data.drawer,
                    timeLeft: data.timeLimit,
                    wordPattern: data.wordPattern || '',
                    word: null,
                    roundNumber: data.roundNumber || prev.roundNumber,
                    totalRounds: data.totalRounds || prev.totalRounds,
                    wordSet: data.wordSet || null,
                    choosing: !!data.choosing,
                    choiceTime: data.choiceTime || 15,
                    wordChoices: null,
                }));
                setGuessIntermission({ active: false, word: null, winner: null, nextDrawer: null, drawer: null, message: '' });
                setMyVote(null);
                setCorrectGuessers(new Set());
                cancelReplay();
                if (!data.choosing) {
                    playSound(data.drawer === user ? 'my_turn' : 'round_begins');
                }
            });
            onGuessCorrect(data => {
                setCorrectGuessers(prev => new Set(prev).add(data.user));
                if (data.user === user) {
                    playSound('correct');
                    confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
                    // Dismiss mobile keyboard, but keep desktop focus on the chat
                    // input so the player can keep typing/chatting.
                    const isTouch = typeof window !== 'undefined'
                        && window.matchMedia
                        && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
                    if (isTouch && document.activeElement) document.activeElement.blur();
                } else {
                    playSound('correct_2');
                }
            });
            onGuessScore(({ pct, votes }) => {
            });
            onGuessRoundEnd(data => {
                const snapshotCorrectGuessers = correctGuessersRef.current;
                const roundDrawer = guessRoundRef.current.drawer;
                const wasDrawer = roundDrawer === user;
                if (!wasDrawer) {
                    if (snapshotCorrectGuessers.has(user)) {
                        playSound('round_ends');
                    } else {
                        playSound('out_of_time');
                    }
                }
                if (wasDrawer) setDrawerEndVersion(v => v + 1);
                setGuessRound(prev => ({ ...prev, active: false, choosing: false, word: null, wordChoices: null }));
                setCorrectGuessers(new Set());
                if (data.word) {
                    setGuessIntermission({
                        active: true,
                        word: data.word,
                        wordSet: data.wordSet || null,
                        winner: data.winner,
                        nextDrawer: data.nextDrawer,
                        drawer: data.drawer || null,
                        message: data.message || '',
                    });
                    setMyVote(null);
                    // Replay the drawing from scratch over 1 second.
                    startReplayAnimation();
                } else {
                    // No word means waiting for players
                    setGuessIntermission({
                        active: false, word: null, winner: null, nextDrawer: null, drawer: null,
                        message: data.message || '',
                    });
                }
            });
            onGuessWord(data => {
                if (data.choices) {
                    // Word choice phase — drawer picks from these
                    setGuessRound(prev => ({ ...prev, wordChoices: data.choices, choosing: true }));
                    playSound('my_turn');
                } else {
                    // Word confirmed — drawing begins
                    setGuessRound(prev => ({ ...prev, word: data.word, wordChoices: null, choosing: false, active: true }));
                }
            });
            onGuessHint(data => {
                setGuessRound(prev => ({ ...prev, wordPattern: data.wordPattern }));
            });
            onGuessUserList(list => {
                setGuessUserList(list);
            });
            onGuessVoteAck(rating => {
                setMyVote(rating);
            });
            onGuessPeriodEnd(data => {
                setGuessRound(prev => ({ ...prev, roundNumber: 0 }));
                setGuessIntermission({
                    active: true, word: null, winner: null, nextDrawer: null, drawer: null,
                    message: data.message || t('roomPage.periodOver'),
                    isPeriodEnd: true,
                });
                // Big confetti if I won the period
                if (data.winner && data.winner === user) {
                    playSound('victory');
                    const end = Date.now() + 3000;
                    const frame = () => {
                        confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 } });
                        confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 } });
                        if (Date.now() < end) requestAnimationFrame(frame);
                    };
                    frame();
                }
            });
            onChatRateLimited(() => {
                setChatRateLimited(true);
                setTimeout(() => setChatRateLimited(false), 1000);
            });
            onKicked((data) => {
                leaveRoom();
                alert(data.global
                    ? t('roomPage.kickedGlobal', 'You have been kicked and banned from all rooms for 1 hour.')
                    : t('roomPage.kicked', 'You have been kicked from this room for 10 minutes.'));
                navigate('/');
            });
            onAiBlocked(() => {
                leaveRoom();
                navigate('/');
            });
            onPublishDrawingAck((data) => {
                setPublishPending(false);
                if (data.success) {
                    const link = data.id ? ` → /gallery/${data.id}` : '';
                    setChatMessages(prev => [...prev, { message: t('drawing.published', 'Drawing saved!') + link, system: true }]);
                } else {
                    setChatMessages(prev => [...prev, { message: data.error || t('drawing.publishFailed', 'Save failed.'), system: true }]);
                }
            });
        }
        return function cleanup() {};
    }, [subscribed, appStep, roomId, user, users, layer, layerList, canvases]);

    // Leave the room on unmount so the backend user count drops immediately.
    useEffect(() => {
        return () => leaveRoom();
    }, []);

    // Redirect to front page if connection is lost while in a room.
    useEffect(() => {
        if (appStep !== 'canvas' || !subscribed) return;
        return onConnectionLostFromRoom(() => {
            const roomPath = window.location.pathname + window.location.search;
            leaveRoom();
            navigate('/', { state: { connectionLost: true, roomPath } });
        });
    }, [appStep, subscribed, navigate]);

    const handleUsernameClose = () => {
        if (user.length < 3) return;
        if (!/^[\w\s\-]{3,15}$/.test(user)) { setNameError(t('roomPage.nameInvalid', 'Only letters, numbers, spaces, - and _ allowed')); return; }
        if (containsProfanity(user)) { setNameError(t('roomPage.nameProfanity', 'Please choose a different name')); return; }
        localStorage.setItem('suttu_nickname', user);
        setAppStep('canvas');
    };

    const togglePrivate = () => {
        setIsPrivate(prev => {
            const next = !prev;
            setRoomPrivate(next);
            sendChatMessage({user, message: next ? 'Room locked 🔒' : 'Room unlocked 🔓'});
            return next;
        });
    };

    const MAX_PUBLISHES = 3;

    const handlePublishClick = useCallback(() => {
        if (publishPending) return;
        setPublishPopup(true);
    }, [publishPending]);

    const handlePublishConfirm = useCallback(() => {
        setPublishPopup(false);
        if (publishPending || publishCount >= MAX_PUBLISHES) return;
        setPublishPending(true);
        setPublishCount(c => c + 1);
        // Capture a fresh thumbnail and send it inline with the publish event
        let thumbnail = null;
        if (canvases.size > 0) {
            const offscreen = document.createElement('canvas');
            offscreen.width = 320;
            offscreen.height = 196;
            const ctx = offscreen.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, 320, 196);
            for (let i = layerList.length - 1; i >= 0; i--) {
                const src = canvases.get(layerList[i]);
                if (src) ctx.drawImage(src, 0, 0, 320, 196);
            }
            thumbnail = offscreen.toDataURL('image/jpeg', 0.55);
        }
        publishDrawing({ thumbnail });
        // Timeout safety: reset pending if no ack within 15s
        setTimeout(() => setPublishPending(false), 15000);
    }, [publishPending, publishCount, canvases, layerList]);

    const handleUserChange = event => {
        setUser(event.target.value.replace(/[^\w\s\-]/g, '').substring(0, 15));
        if (nameError) setNameError('');
    };

    const sendLocalStrokes = useCallback(() => {
        const strokes = localStrokesRef.current;
        if (strokes.length === 0) return;
        sendStrokes({l: layerRef.current, d: strokes});
        localStrokesRef.current = [];
    }, []);

    // Clear prediction canvas and redraw any remaining pending predicted strokes.
    const reconcilePrediction = () => {
        const predCanvas = predictionCanvasRef.current;
        if (!predCanvas) return;
        const ctx = predCanvas.getContext('2d');
        ctx.clearRect(0, 0, w, h);
        for (const s of pendingStrokesRef.current) {
            const predMap = new Map([[s.l, predCanvas]]);
            drawLine(predMap, s, s.t);
        }
    };

    // Cancel any in-flight replay animation.
    const cancelReplay = () => {
        if (replayAnimRef.current) {
            cancelAnimationFrame(replayAnimRef.current);
            replayAnimRef.current = null;
        }
        setReplayActive(false);
    };

    // Replay all strokes from scratch over ~1 second during intermission.
    const startReplayAnimation = () => {
        cancelReplay();
        // Snapshot the current stroke history (all layers, flattened in order).
        const allStrokes = [];
        for (const [, layerStrokes] of strokeHistoryRef.current) {
            allStrokes.push(...layerStrokes);
        }
        allStrokes.sort((a, b) => a.sid - b.sid);
        if (allStrokes.length === 0) return;

        playSound('scribble_3s');
        setReplayActive(true);

        // Clear all canvases.
        canvases.forEach(canvas => {
            canvas.getContext('2d').clearRect(0, 0, w, h);
        });

        const duration = 3000; // 3 seconds
        const startTime = performance.now();
        let drawn = 0;

        const frame = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // How many strokes should be visible at this point.
            const target = Math.floor(progress * allStrokes.length);

            // Draw all strokes from `drawn` up to `target`.
            for (let i = drawn; i < target; i++) {
                const s = allStrokes[i];
                if (!s) continue;
                drawLine(canvases, s, s.t);
            }
            drawn = target;

            if (progress < 1) {
                replayAnimRef.current = requestAnimationFrame(frame);
            } else {
                replayAnimRef.current = null;
                setReplayActive(false);
            }
        };

        replayAnimRef.current = requestAnimationFrame(frame);
    };

    const addLocalStroke = useCallback((stroke, predicted) => {
        const fullStroke = {l: layerRef.current, ...stroke};
        localStrokesRef.current = [...localStrokesRef.current, fullStroke];
        if (predicted) pendingStrokesRef.current.push(fullStroke);
    }, []);

    const onLocalUndo = useCallback(() => {
        localStrokesRef.current = [];
        currentStrokeRef.current = null;
        pendingStrokesRef.current = [];
        reconcilePrediction();
    }, []);

    const moveLayer = useCallback((fromIndex, toIndex) => {
        setLayerList(layers => {
            const newLayers = arrayMove(layers, fromIndex, toIndex);
            sendMoveLayers(newLayers);
            return newLayers;
        });
    }, []);



    const toggleOptIn = useCallback(() => {
        setSelfOptedIn(prev => {
            const next = !prev;
            sendGuessOptIn(next);
            return next;
        });
    }, []);

    // Derive AFK state for the local user from guessUserList
    const selfAfk = (() => {
        for (let i = 0; i < guessUserList.length; i++) {
            if (guessUserList[i].name === user) return !!guessUserList[i].afk;
        }
        return false;
    })();

    const toggleAfk = useCallback((wantAfk) => {
        sendAfkToggle(!!wantAfk);
    }, []);

    const toggleAiAllowed = useCallback(() => {
        if (!aiAvailable) return;
        setRoomAiAllowed(!aiAllowed);
    }, [aiAllowed, aiAvailable]);

    return (
        <div className="App" style={{overflow: 'hidden', minHeight: '100vh'}}>
            <PageBackground static />
            {appStep === 'canvas' &&
                <div>
                    <Drawing
                        canvasRef={canvasRef}
                        canvases={canvases}
                        users={users}
                        localStrokesRef={localStrokesRef}
                        currentStrokeRef={currentStrokeRef}
                        sendLocalStrokes={sendLocalStrokes}
                        predictionCanvasRef={predictionCanvasRef}
                        addLocalStroke={addLocalStroke}
                        onLocalUndo={onLocalUndo}
                        layerList={layerList}
                        layer={layer}
                        setLayer={setLayer}
                        moveLayer={moveLayer}
                        thumbs={thumbs}
                        thumbRef={thumbRef}
                        canvasVersion={canvasVersion}
                        clearVersion={clearVersion}
                        room={roomId}
                        isPrivate={isPrivate}
                        togglePrivate={togglePrivate}
                        isOwner={user === roomOwner}
                        roomOwner={roomOwner}
                        chatMessages={chatMessages}
                        user={user}
                        // Guess-mode props
                        isGuessMode={isGuessMode}
                        drawingDisabled={drawingDisabled}
                        guessRound={guessRound}
                        guessIntermission={guessIntermission}
                        guessUserList={guessUserList}
                        selfOptedIn={selfOptedIn}
                        toggleOptIn={toggleOptIn}
                        selfAfk={selfAfk}
                        toggleAfk={toggleAfk}
                        aiAvailable={aiAvailable}
                        aiAllowed={aiAllowed}
                        toggleAiAllowed={toggleAiAllowed}
                        isDrawer={isDrawer}
                        drawerEndVersion={drawerEndVersion}
                        everyoneDraws={effectiveEveryoneDraws}
                        roomSettings={roomSettings}
                        roundActive={guessRound.active}
                        isSpectator={isSpectator}
                        onWordChoice={sendWordChoice}
                        myVote={myVote}
                        onVote={sendGuessVote}
                        onSkip={sendGuessSkip}
                        rateLimited={chatRateLimited}
                        setRateLimited={setChatRateLimited}
                        correctGuessers={correctGuessers}
                        onVoteKick={sendVoteKick}
                        onTransferOwnership={sendTransferOwnership}
                        onPublish={isGuessMode || drawingDisabled || publishCount >= MAX_PUBLISHES || user !== roomOwner || !hasEnoughStrokes ? undefined : handlePublishClick}
                        publishPending={publishPending}
                        homeButton={
                            <span
                                onClick={() => navigate('/')}
                                style={{
                                    cursor: 'pointer', color: '#bbc', fontFamily: 'monospace',
                                    fontSize: 13, fontWeight: 700, letterSpacing: 1,
                                    userSelect: 'none',
                                }}
                                title={t('roomPage.backToLobby')}
                            >suttu.io</span>
                        }
                    />
                </div>
            }

            {/* Publish confirmation dialog */}
            <Dialog open={publishPopup} onClose={() => setPublishPopup(false)}
                    fullWidth maxWidth="xs"
                    PaperProps={{style: {background: '#2a2a3e', color: '#eee', borderRadius: 12, border: '1px solid #555', maxHeight: 'calc(100vh - 20px)', margin: 10, display: 'flex', flexDirection: 'column'}}}
                    disableScrollLock>
                <DialogTitle style={{fontWeight: 600, flexShrink: 0}}>{t('drawing.publishTitle', 'Publish Drawing')}</DialogTitle>
                <DialogContent style={{overflowY: 'auto', flex: 1}}>
                    <DialogContentText style={{color: '#ccc'}}>
                        {publishCount >= MAX_PUBLISHES
                            ? t('drawing.publishLimitReached', 'You have reached the publish limit for this room.')
                            : t('drawing.publishConfirm', {
                                remaining: MAX_PUBLISHES - publishCount,
                                defaultValue: 'Save this drawing to the gallery? You have {{remaining}} publish(es) remaining in this room.',
                            })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{flexShrink: 0}}>
                    <Button onClick={() => setPublishPopup(false)} style={{color: '#aab'}}>
                        {t('drawing.cancel', 'Cancel')}
                    </Button>
                    {publishCount < MAX_PUBLISHES && (
                        <Button onClick={handlePublishConfirm} variant="contained"
                                style={{background: '#7755cc', color: '#fff', fontWeight: 600}}>
                            {t('drawing.publishNow', 'Publish')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Username dialog */}
            <Dialog open={appStep === 'username'} aria-labelledby="username-dialog-title"
                    fullWidth maxWidth="xs"
                    PaperProps={{style: {background: '#2a2a3e', color: '#eee', borderRadius: 12, border: '1px solid #555', maxHeight: 'calc(100vh - 20px)', margin: 10, display: 'flex', flexDirection: 'column'}}}
                    disableScrollLock>
                <DialogTitle id="username-dialog-title" style={{fontWeight: 600, flexShrink: 0}}>{t('roomPage.identifyYourself')}</DialogTitle>
                <DialogContent style={{overflowY: 'auto', flex: 1}}>
                    <DialogContentText style={{color: '#ccc'}}>
                        {t('roomPage.enterUsername')} <strong style={{fontFamily: 'monospace', letterSpacing: 2}}>{roomId}</strong>
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('roomPage.namePlaceholder')}
                        value={user}
                        onChange={handleUserChange}
                        onFocus={() => { if (isAutoName) { setUser(''); setIsAutoName(false); } }}
                        onKeyPress={e => { if (e.key === 'Enter') handleUsernameClose(); }}
                        error={!!nameError}
                        helperText={nameError}
                        fullWidth
                        variant="outlined"
                        InputProps={{style: {color: '#eee'}}}
                        InputLabelProps={{style: {color: '#99a'}}}
                        sx={{'& .MuiOutlinedInput-root': {'& fieldset': {borderColor: '#555'}, '&:hover fieldset': {borderColor: '#888'}, '&.Mui-focused fieldset': {borderColor: '#7755cc'}}}}
                    />
                    {gameType === 'guess' && (
                        <label style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 14, color: '#bbc', cursor: 'pointer'}}>
                            <input type="checkbox" checked={isSpectator} onChange={e => setIsSpectator(e.target.checked)} />
                            {t('roomPage.spectateToggle', 'Spectate / TV mode')}
                        </label>
                    )}
                </DialogContent>
                <DialogActions style={{flexShrink: 0}}>
                    <Button onClick={() => navigate('/')} style={{color: '#aab', textTransform: 'none'}}>
                        {t('roomPage.back')}
                    </Button>
                    <Button onClick={handleUsernameClose} variant="contained" disabled={user.length < 3}
                            style={{background: '#7755cc', color: '#fff', fontWeight: 600, textTransform: 'none'}}>
                        {t('roomPage.join')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
